<template>
<div>
    <section class="section-border border-primary">
        <div class="container d-flex flex-column">
            <div class="row align-items-center justify-content-center gx-0 min-vh-100">
                <div class="col-8 col-md-6 col-lg-7 offset-md-1 order-md-2 mt-auto mt-md-0 pt-12 pb-4 py-md-11">
                    <img src="/assets/img/illustrations/illustration-2.png" alt="..." class="img-fluid">
                </div>
                <div class="col-12 col-md-5 col-lg-4 order-md-1 mb-auto mb-md-0 pb-8 py-md-11">
                    <h1 class="mb-0 fw-bold text-center">Login</h1>
                    <p class="mb-6 text-center text-muted" v-if="!tfaRequired && Object.keys(errors).length < 1">
                        You will get access to the dashboard
                    </p>
                    <p class="mb-6 text-center text-danger" v-for="error in errors.__all__" :key="error">{{ error }}</p>
                    <form class="mb-6" @submit.prevent="login" v-if="!tfaRequired">
                        <div class="form-group">
                            <label class="form-label" for="email">Email</label>
                            <input type="email" class="form-control"
                                   placeholder="Enter your Email"
                                   :class="{'is-invalid': errors.email}"
                                v-model="form.email" v-focus>
                            <small class="form-text text-danger" v-for="error in errors.email" :key="error">{{ error }}</small>
                        </div>
                        <div class="form-group mb-5">
                            <label class="form-label" for="password">Password</label>
                            <input type="password" class="form-control"
                                placeholder="Enter your password"
                                :class="{'is-invalid': errors.password}"
                                v-model="form.password">
                            <small class="form-text text-danger" v-for="error in errors.password" :key="error">{{ error }}</small>
                        </div>
                        <div class="form-group mb-5 fs-sm">
                            <label class="d-block" :class="{'is-invalid': errors.agree_terms_of_service}">
                                <input type="checkbox" class="form-check-input" v-model="form.agree_terms_of_service">
                                I accept the
                                <a href="/help/terms-of-service">Terms of service</a>
                            </label>
                            <label class="d-block" :class="{'is-invalid': errors.agree_privacy_policy}">
                                <input type="checkbox" class="form-check-input" v-model="form.agree_privacy_policy">
                                I accept the
                                <a href="/help/privacy-policy">Privacy Policy</a>
                            </label>
                        </div>
                        <button class="btn w-100 btn-primary" type="submit" :disabled="disabled">Enter</button>
                    </form>
                    <form class="mb-6" @submit.prevent="verify" v-if="tfaRequired">
                        <p class="mb-4 text-success text-center" v-if="resetNotice">
                            Password changed. Enter the code from your authenticator to finish signing in.
                        </p>
                        <p class="mb-4 text-muted text-center">
                            Enter the 6-digit code from your authenticator app,
                            or one of your recovery codes.
                        </p>
                        <div class="form-group mb-5">
                            <label class="form-label" for="tfa-login-code">Code</label>
                            <input type="text" class="form-control" id="tfa-login-code" inputmode="numeric"
                                   autocomplete="one-time-code" placeholder="123456" maxlength="32"
                                   :class="{'is-invalid': errors.code}" v-model="tfaCode" v-focus>
                            <small class="form-text text-danger" v-for="error in errors.code" :key="error">{{ error }}</small>
                        </div>
                        <button class="btn w-100 btn-primary" type="submit" :disabled="disabled || !tfaCode.trim()">Verify</button>
                        <button class="btn w-100 btn-link" type="button" @click="backToCredentials">Back</button>
                    </form>
                    <!-- v-show, not v-if: the Telegram widget is mounted into
                         $refs.tgLoginWidget once, so the node must stay in the DOM
                         even while the second login step is on screen. -->
                    <div v-show="!tfaRequired">
                        <div class="d-flex align-items-center my-4">
                            <hr class="flex-grow-1"><span class="mx-3 text-muted small">or</span><hr class="flex-grow-1">
                        </div>
                        <div class="text-center mb-4" ref="tgLoginWidget"></div>
                        <p class="mb-0 fs-sm text-center text-muted">
                            Don't have an account yet?
                            <router-link :to="{path: '/registration', query: safeNextRaw ? {next: safeNextRaw} : {}}">Register</router-link>.
                        </p>
                        <p class="mb-0 fs-sm text-center text-muted">
                            Or you can
                            <router-link :to="{path: '/forgot'}">reset your password</router-link>
                            if you forgot
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import { focus } from "utils/focus"
import { EventBus } from "@/bus";
import { displayErrors } from "utils/string"
import { safeNext, navigateToNext } from "utils/redirect"
import { tgLoginMixin } from "utils/tg-login"
export default {
    directives: { focus },
    mixins: [tgLoginMixin],
    data() {
        return {
            form: {
                email: "",
                password: "",
                agree_terms_of_service: true,
                agree_privacy_policy: true
            },
            errors: {},
            disabled: false,
            // Second login step: the server answered {state:false,
            // message:"tfa_required"} and set a 300s pre-auth cookie.
            tfaRequired: false,
            tfaCode: "",
            // Set right after a successful /tfa/verify while we wait for
            // context:loaded to confirm the session (see tfaContextFailed).
            tfaAwaitingContext: false,
            tfaContextTimer: null
        }
    },
    computed: {
        safeNextRaw() {
            return safeNext(this.$route.query.next)
        },
        // Reset.vue redirects here with ?reset=1 when the account has 2FA on,
        // so the password-changed confirmation is never otherwise shown.
        resetNotice() {
            return this.tfaRequired && this.$route.query.reset === '1'
        }
    },
    created() {
        EventBus.on('context:loaded', context => {
            if (context.id) {
                this.clearTfaTimer()
                this.tfaAwaitingContext = false
                this.redirectAfterAuth()
                return
            }
            // No id: if a TOTP code was just verified, this is a dead end —
            // the code is already spent and there is no way back to the
            // credentials step on its own, so surface it instead of leaving
            // a blank code form with no explanation.
            if (this.tfaAwaitingContext) this.tfaContextFailed()
        })
        EventBus.emit('context:request')
        if (this.$route.query.step === 'tfa') this.tfaRequired = true
    },
    watch: {
        // The Telegram widget on this very page pushes /login?step=tfa, which
        // reuses the mounted component — created() will not run again.
        '$route.query.step'(step) {
            if (step === 'tfa') this.tfaRequired = true
        }
    },
    unmounted() {
        this.clearTfaTimer()
    },
    methods: {
        clearTfaTimer() {
            if (this.tfaContextTimer) {
                clearTimeout(this.tfaContextTimer)
                this.tfaContextTimer = null
            }
        },
        redirectAfterAuth() {
            const next = this.safeNextRaw
            if (!next) {
                this.$router.replace({path: '/dashboard'})
                return
            }
            // A `next` into /oauth/authorize needs a freshly minted nav nonce
            // (see utils/redirect.js); if it cannot be minted we stay put and
            // say so, rather than sending the user into a silent login loop.
            navigateToNext(this.$services, next).catch(resp => {
                displayErrors(resp, this)
                this.disabled = false
            })
        },
        login() {
            this.disabled = true
            this.errors = {}
            this.$services.Login().save(this.form).then(resp => {
                this.errors = {}
                // tfa_required arrives with state=false, so it must be handled
                // before the failure branch below — displayErrors would
                // otherwise paint its "Request failed" fallback over it.
                if (resp.body.message === 'tfa_required') {
                    this.tfaRequired = true
                    this.tfaCode = ""
                    this.disabled = false
                    return
                }
                if (resp.body.state) {
                    EventBus.emit('context:changed')
                    setTimeout(() => {
                        this.redirectAfterAuth()
                    })
                }
                displayErrors(resp, this);
                this.disabled = false
            }).catch(resp => {
                displayErrors(resp, this);
                this.disabled = false
            })
            setTimeout(() => {
                this.disabled = false
            }, 10000)
        },
        verify() {
            if (!this.tfaCode.trim()) return
            this.disabled = true
            this.errors = {}
            this.$services.TfaVerify().save({ code: this.tfaCode.trim() }).then(resp => {
                if (resp.body.state) {
                    // Unlike login(), this step does NOT redirect on its own:
                    // context:loaded with a real id is what moves us on, so
                    // nothing reaches the dashboard (or ?next=) without a 200
                    // from /tfa/verify AND a server-confirmed session.
                    // tfaContextFailed() covers the case where that never
                    // arrives (see created()).
                    this.tfaAwaitingContext = true
                    this.clearTfaTimer()
                    this.tfaContextTimer = setTimeout(() => this.tfaContextFailed(), 8000)
                    EventBus.emit('context:changed')
                } else {
                    displayErrors(resp, this)
                    this.disabled = false
                }
            }).catch(resp => {
                if (resp.status === 401) {
                    // The pre-auth cookie lives 300s; once it is gone the only
                    // way forward is the first step again.
                    this.backToCredentials()
                    this.errors = {'__all__': ["Your login session expired, please sign in again"]}
                } else {
                    displayErrors(resp, this)
                    // 403 = wrong code: show it under the field, not above the form.
                    if (resp.status === 403 && this.errors.__all__) {
                        this.errors = { code: this.errors.__all__ }
                    }
                }
                this.disabled = false
            })
        },
        // Fires when a verified TOTP code never turned into a loaded context:
        // either context:loaded arrived without an id, or the 8s timeout above
        // ran out first. The code is already spent, so this is a dead end —
        // tell the user instead of leaving a disabled, silent form up.
        tfaContextFailed() {
            if (!this.tfaAwaitingContext) return
            this.tfaAwaitingContext = false
            this.clearTfaTimer()
            this.errors = {'__all__': ["Signed in, but the dashboard could not be loaded — reload the page"]}
            this.disabled = false
        },
        backToCredentials() {
            this.tfaAwaitingContext = false
            this.clearTfaTimer()
            this.tfaRequired = false
            this.tfaCode = ""
            this.errors = {}
            if (this.$route.query.step) {
                // Keep ?next= so an OAuth consent round-trip is not lost by
                // stepping back to the credentials form.
                const query = this.safeNextRaw ? {next: this.safeNextRaw} : {}
                this.$router.replace({path: '/login', query})
            }
        }
    }
}
</script>
<style scoped>
label.is-invalid { color: red }
.is-invalid a { color: brown }
</style>
